import { useCallback, useRef } from 'react'
import { useHistory } from '@fs/zion-router'
import { useContainerWidth } from '@fs/zion-ui'
import { usePerson } from '@fs/zion-person-service'
import { useSplashTransition } from '../providers/SplashTransitionProvider'
import { selectAnimationImage, isValidClickForAnimation } from '../helpers/animationHelpers'
import colorsSet from '../pages/Activity/colors'

export default function useCardTransition(data) {
  const {
    url,
    dataType,
    colorTheme,
    imageUrl: dataImageUrl,
    imageUrlSvg,
    person,
    splash,
    disableSplashTransition = false,
  } = data || {}
  const cardRef = useRef(null)
  const history = useHistory()
  const { startTransition } = useSplashTransition()

  // Some activity groups are informational (backend `infoDataType` or `splash: informational`) and should not
  // run the fly-to-splash transition. Avatar (static) also opts out via `disableSplashTransition`.
  const shouldDisableSplashTransition =
    disableSplashTransition || splash === 'informational' || dataType === 'informational'

  // For person cards, fetch the portrait URL
  const personData = usePerson(person?.personId, 'card', null, true)
  const portraitUrl = personData?.portraitUrl ?? person?.portraitUrl

  // Use zion-ui container width to match SplashBase logic exactly
  const atWidth = useContainerWidth()
  const targetIconWidth = Number.parseInt(atWidth({ default: '180px', md: '330px' }), 10)

  const handleTransitionClick = useCallback(
    (event, overrideUrl = null) => {
      if (shouldDisableSplashTransition) {
        return
      }

      const targetUrl = overrideUrl || url

      // Validate click event (check for primary click and valid animation conditions)
      if (!isValidClickForAnimation(event, splash)) {
        return
      }

      event.preventDefault()

      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect()
        const backgroundColor = colorsSet[colorTheme]?.card?.primary

        // Select best available image for animation
        const finalImageUrl = selectAnimationImage(imageUrlSvg, dataImageUrl, portraitUrl)

        startTransition({
          url: targetUrl,
          cardRect: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          },
          imageUrl: finalImageUrl,
          colorTheme: backgroundColor,
          targetIconWidth,
        })
      } else {
        // Fallback if ref is missing
        history.push(targetUrl)
      }
    },
    [
      shouldDisableSplashTransition,
      colorTheme,
      startTransition,
      url,
      dataImageUrl,
      imageUrlSvg,
      portraitUrl,
      targetIconWidth,
      history,
      splash,
    ]
  )

  return {
    cardRef,
    handleTransitionClick,
  }
}
